import { useQuery } from '@tanstack/react-query';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { partnerManagementService } from '~/services';
import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service';
import type { ContextTableSummary } from '~/store/fulfillmentContext/helpers';
import { buildContextTableSummaries } from '~/store/fulfillmentContext/helpers';
import type { AssetTag, FulfillmentContext, Schedule } from '../../store/services/FP_FulfillmentContextService/types';

export const FULFILLMENT_CONTEXT_COMPACT_QUERY_KEY = 'fulfillmentContextsCompact';

// Create a wrapper query that fetches all required data
const fulfillmentContextsQuery = {
  queryKey: FULFILLMENT_CONTEXT_COMPACT_QUERY_KEY,
  configurationType: 'cached' as const,
  fn: async (): Promise<ContextTableSummary[]> => {
    const [fulfillmentContexts, contacts, addresses, origins, schedules, tenants, tags] = await Promise.all([
      partnerManagementService.fulfillmentContexts.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
      partnerManagementService.contacts.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
      partnerManagementService.addresses.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
      partnerManagementService.origins.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
      partnerManagementService.schedules.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
      partnerManagementService.tenants.search.fn(),
      partnerManagementService.tags.search.fn({ count: ASSET_LIST_PAGE_SIZE }),
    ]);

    // Build the table summaries using the same helper function
    return buildContextTableSummaries({
      contexts: fulfillmentContexts as FulfillmentContext[], // Cast to FulfillmentContext[] due to type differences
      contacts,
      addresses,
      origins,
      schedules: schedules as Schedule[], // Cast to Schedule[] due to type differences
      tenants,
      tags: tags as AssetTag[], // Cast to AssetTag[] due to type differences
    });
  },
};

interface UseFulfillmentContextsOptions<TData = ContextTableSummary[]> {
  partnerId: string | undefined;
  select?: (data: ContextTableSummary[]) => TData;
}

export const useFulfillmentContexts = <TData = ContextTableSummary[]>(
  options: UseFulfillmentContextsOptions<TData>,
) => {
  const { partnerId, select } = options;

  const queryOptions = useQueryCreator(fulfillmentContextsQuery, {
    args: [],
  });

  return useQuery({
    ...queryOptions,
    refetchOnMount: true,
    enabled: !!partnerId, // Only run query if we have a partner ID
    select,
  });
};
